import { useEffect, useState } from 'react';
import { getAccessTokenSilently } from '@/components/auth/Auth0Provider';
import { INTEGRATIONS_REPORTING_API_URL } from '@/api/integrations';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const HEARTBEAT_WINDOW_MS = 30 * 60 * 1000;

export type DtHeartbeatMaps = {
  /** Keyed by external_device_id (= DT device_id from heartbeat response). */
  sensorMap: Record<string, true>;
  /** Keyed by external_gateway_id (= cloud_connector_id from heartbeat response). */
  gatewayMap: Record<string, true>;
  /** Maps external_device_id → cloud_connector_id (last gateway the sensor streamed through). */
  sensorToGatewayMap: Record<string, string>;
};

/** Parses the raw Reporting API JSON into DtHeartbeatMaps.
 *  Iterates every time-bucket point so that:
 *  - a device active in any bucket is marked as seen (sensorMap/gatewayMap)
 *  - sensorToGatewayMap retains the cloud_connector_id from the *latest* point
 *    (later entries overwrite earlier ones — arrays are ordered oldest→newest)
 *
 *  Note: cloud_connector_id from dt_heartbeat == external_gateway_id in the
 *  Integrations API. They share the same DT ID namespace. */
function parseDtHeartbeatResponse(json: {
  data?: Record<string, Array<{ device_id?: string; cloud_connector_id?: string }>>;
}): DtHeartbeatMaps {
  const sensorMap: Record<string, true> = {};
  const gatewayMap: Record<string, true> = {};
  const sensorToGatewayMap: Record<string, string> = {};
  const data = json.data ?? {};

  for (const [, points] of Object.entries(data)) {
    for (const point of points) {
      const deviceId = point.device_id;
      const connectorId = point.cloud_connector_id;
      if (deviceId) {
        sensorMap[deviceId] = true;
        // Later points overwrite earlier ones — retains the most-recent gateway
        if (connectorId) sensorToGatewayMap[deviceId] = connectorId;
      }
      if (connectorId) gatewayMap[connectorId] = true;
    }
  }

  return { sensorMap, gatewayMap, sensorToGatewayMap };
}

async function fetchHeartbeats(spaceId: string): Promise<DtHeartbeatMaps> {
  const token = await getAccessTokenSilently();
  const stop = new Date();
  const start = new Date(stop.getTime() - HEARTBEAT_WINDOW_MS);

  const res = await fetch(INTEGRATIONS_REPORTING_API_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      measurements: ['dt_heartbeat'],
      items: [spaceId],
      start: start.toISOString(),
      stop: stop.toISOString(),
      interval: '15m',
    }),
  });

  if (!res.ok) {
    throw new Error(`Reporting API responded with ${res.status}`);
  }

  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.description ?? 'Reporting API error');
  }

  return parseDtHeartbeatResponse(json);
}

export function useGetDtHeartbeats({ spaceId }: { spaceId?: string }) {
  const [heartbeatMaps, setHeartbeatMaps] = useState<DtHeartbeatMaps | undefined>(undefined);

  useEffect(() => {
    if (!spaceId) return;

    setHeartbeatMaps(undefined);
    let cancelled = false;

    const poll = () => {
      fetchHeartbeats(spaceId)
        .then((maps) => {
          if (!cancelled) setHeartbeatMaps(maps);
        })
        .catch((err) => {
          console.error('[useGetDtHeartbeats] fetch failed:', err);
        });
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [spaceId]);

  return heartbeatMaps;
}

async function fetchHeartbeatsAll(floorIds: string[]): Promise<DtHeartbeatMaps> {
  const token = await getAccessTokenSilently();
  const stop = new Date();
  const start = new Date(stop.getTime() - HEARTBEAT_WINDOW_MS);

  const res = await fetch(INTEGRATIONS_REPORTING_API_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      measurements: ['dt_heartbeat'],
      items: floorIds,
      start: start.toISOString(),
      stop: stop.toISOString(),
      interval: '15m',
    }),
  });

  if (!res.ok) {
    throw new Error(`Reporting API responded with ${res.status}`);
  }

  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.description ?? 'Reporting API error');
  }

  return parseDtHeartbeatResponse(json);
}

/** Fetches heartbeats across all floors in a single Reporting API request.
 *  Polls every 5 min (same cadence as useGetDtHeartbeats). */
export function useGetDtHeartbeatsAll({ floorIds }: { floorIds: string[] }) {
  const [heartbeatMaps, setHeartbeatMaps] = useState<DtHeartbeatMaps | undefined>(undefined);

  // Stable string key avoids resetting the poll interval on every render
  const stableKey = floorIds.join(',');

  useEffect(() => {
    if (!stableKey) return;

    setHeartbeatMaps(undefined);
    let cancelled = false;
    const ids = stableKey.split(',').filter(Boolean);

    const poll = () => {
      fetchHeartbeatsAll(ids)
        .then((maps) => {
          if (!cancelled) setHeartbeatMaps(maps);
        })
        .catch((err) => {
          console.error('[useGetDtHeartbeatsAll] fetch failed:', err);
        });
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [stableKey]);

  return heartbeatMaps;
}
